import React, { useState, useEffect, useMemo } from "react"
import { useKindeAuth } from "@kinde-oss/kinde-auth-react"
import { Switch, Tooltip, Input, Modal } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faArchive,
  faFolder,
  faFolderPlus,
  faSearch,
} from "@fortawesome/free-solid-svg-icons"

import ProjectTable from "./ProjectTable/ProjectTable.component"
import AddProjectModal from "./CreateProject/AddProjectModal.component"
import NewFolderModal from "./Folders/NewFolderModal.component"
import MoveToFolderModal from "./Folders/MoveToFolderModal.component"
import RenameFolderModal from "./Folders/RenameFolderModal.component"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import {
  ProjectListResponse,
  ArchivedProjectsResponse,
  PROJECT_FILTER,
  ProjectData,
} from "types/project.types"
import { KindeRole, KindeRolesClaim } from "types/kinde.types"
import { hasAdminPrivileges } from "types/userRole.types"
import { buildProjectTree } from "utils/projectTree"
import { useDeleteProjectFolderMutation } from "features/Projects/api/useProjectsMutations"
import { notify } from "shared/ui/Notifications/notify"

interface ProjectDisplayProps {
  filter: PROJECT_FILTER
  setFilter: (filter: PROJECT_FILTER) => void
  getProjects: () => Promise<void> | void
  getArchivedProjects: () => Promise<void> | void
  isLoading: boolean
  isLoadingArchived: boolean
  projects: ProjectListResponse
  archivedProjects: ArchivedProjectsResponse
  folders: string[]
  handleUnarchive: (projectId: string, owner: string) => Promise<void> | void
  handleArchive: (projectId: string, owner: string) => Promise<void> | void
}

const ProjectDisplay: React.FC<ProjectDisplayProps> = ({
  filter,
  setFilter,
  getProjects,
  getArchivedProjects,
  isLoading,
  isLoadingArchived,
  projects,
  archivedProjects,
  folders,
  handleUnarchive,
  handleArchive,
}) => {
  const [showArchived, setShowArchived] = useState<boolean>(false)
  const [search, setSearch] = useState<string>("")
  const { getClaim } = useKindeAuth()
  const [isAdmin, setIsAdmin] = useState<boolean>(false)

  const deleteFolderMutation = useDeleteProjectFolderMutation()

  const [newFolderOpen, setNewFolderOpen] = useState<boolean>(false)
  const [moveProject, setMoveProject] = useState<ProjectData | null>(null)
  const [renameFolder, setRenameFolder] = useState<string | null>(null)

  useEffect(() => {
    const loadUserRoles = async () => {
      try {
        const userRoles = (await getClaim("roles")) as KindeRolesClaim | null
        const adminStatus =
          userRoles?.value?.some((role: KindeRole) =>
            hasAdminPrivileges(role.key)
          ) || false
        setIsAdmin(adminStatus)
      } catch (error) {
        console.error("Failed to load user roles:", error)
        setIsAdmin(false)
      }
    }
    loadUserRoles()
  }, [getClaim])

  const toggleFilter = (): void => {
    setFilter(
      filter === PROJECT_FILTER.OWNED
        ? PROJECT_FILTER.MEMBER
        : PROJECT_FILTER.OWNED
    )
  }

  const viewAllProjects = (): void => {
    setFilter(PROJECT_FILTER.ALL)
  }

  const handleSwitchChange = (checked: boolean): void => {
    setShowArchived(checked)
    if (checked) {
      getArchivedProjects()
    } else {
      getProjects()
    }
  }

  const treeRows = useMemo(
    () =>
      buildProjectTree(
        showArchived
          ? archivedProjects?.result || []
          : projects?.result || [],
        folders
      ),
    [showArchived, archivedProjects, projects, folders]
  )

  const handleDeleteFolder = (folderPath: string, count: number): void => {
    const confirmDelete = async () => {
      try {
        await deleteFolderMutation.mutateAsync(folderPath)
        notify.success(
          "Folder deleted",
          count > 0
            ? `Deleted '${folderPath}'. ${count} project(s) moved to ungrouped.`
            : `Deleted '${folderPath}'.`
        )
      } catch (error) {
        const message = error instanceof Error ? error.message : "Unknown error"
        notify.error("Error deleting folder", message)
      }
    }

    if (count === 0) {
      confirmDelete()
      return
    }

    Modal.confirm({
      title: `Delete folder '${folderPath}'?`,
      content: `This folder contains ${count} project(s). They will become ungrouped.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: confirmDelete,
    })
  }

  return (
    <div className="flex flex-col p-5">
      <div className="flex flex-row items-center justify-between mb-2">
        <div className="flex items-center">
          <h1 className="text-3xl font-semibold m-0 mr-4">
            {showArchived
              ? "Archived Projects"
              : `Projects - ${
                  filter.charAt(0).toUpperCase() + filter.slice(1)
                }`}
          </h1>
          <Tooltip
            title={
              showArchived
                ? "Switch to active projects"
                : "Switch to archived projects"
            }
            placement="right"
          >
            <Switch
              checked={showArchived}
              onChange={handleSwitchChange}
              checkedChildren={<FontAwesomeIcon icon={faArchive} />}
              unCheckedChildren={<FontAwesomeIcon icon={faFolder} />}
              style={{ backgroundColor: "#36c3ed" }}
              className="mt-1"
            />
          </Tooltip>
        </div>
        <div className="flex items-center space-x-2 my-4 h-5">
          <Input
            allowClear
            prefix={<FontAwesomeIcon icon={faSearch} />}
            placeholder="Search projects"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 220 }}
          />
          {!showArchived && (
            <>
              <DataQButton type="primary" onClick={toggleFilter}>
                {filter === PROJECT_FILTER.OWNED
                  ? "View Member Projects"
                  : "View Owned Projects"}
              </DataQButton>
              {isAdmin && (
                <DataQButton type="secondary" onClick={viewAllProjects}>
                  View All Projects
                </DataQButton>
              )}
              <DataQButton
                type="secondary"
                onClick={() => setNewFolderOpen(true)}
              >
                New Folder <FontAwesomeIcon icon={faFolderPlus} className="ml-2" />
              </DataQButton>
              <AddProjectModal />
            </>
          )}
        </div>
      </div>

      <ProjectTable
        rows={treeRows}
        searchTerm={search}
        isLoading={showArchived ? isLoadingArchived : isLoading}
        isArchived={showArchived}
        handleUnarchive={handleUnarchive}
        handleArchive={handleArchive}
        onRenameFolder={(folderPath) => setRenameFolder(folderPath)}
        onDeleteFolder={handleDeleteFolder}
        onMoveProject={(project) => setMoveProject(project)}
      />

      <NewFolderModal
        open={newFolderOpen}
        onClose={() => setNewFolderOpen(false)}
        existingFolders={folders}
      />
      <MoveToFolderModal
        open={moveProject !== null}
        onClose={() => setMoveProject(null)}
        project={moveProject}
        existingFolders={folders}
      />
      <RenameFolderModal
        open={renameFolder !== null}
        onClose={() => setRenameFolder(null)}
        folderPath={renameFolder}
      />
    </div>
  )
}

export default ProjectDisplay
